import { Badge, Button, Card } from 'react-bootstrap';
import { FEMA_CREDENTIAL_ACTION_LABELS } from '../../config/femaCredentials.config';
import {
  describeCredentialState,
  environmentClassBadgeVariant,
  environmentClassLabel,
} from '../../utils/credentialState.utils';
import CredentialFacts from '../CredentialFacts/CredentialFacts';

/**
 * ONE status card per held class (design read §3.3). It answers "Can we send IPAWS alerts
 * now?" and offers the actions of the state. It shows the active certificate and, until the
 * swap, the replacement too (§3.5).
 *
 * @param {{
 *   tenant: import('types/femaTenant.types').FemaTenantSummary,
 *   busy?: boolean,
 *   onAction: (action: import('../../config/femaCredentials.config').FemaCredentialAction,
 *     tenant: import('types/femaTenant.types').FemaTenantSummary) => void,
 * }} props
 */
const CredentialStatusCard = ({ tenant, busy = false, onAction }) => {
  const description = describeCredentialState(tenant);
  const classLabel = environmentClassLabel(tenant.environment);
  const headingId = `fema-credential-${tenant.id}-heading`;

  return (
    <Card className="mb-3" border={description.variant} as="section" aria-labelledby={headingId}>
      <Card.Header className="d-flex flex-wrap align-items-center gap-2">
        <h2 id={headingId} className="h5 mb-0">
          {tenant.cogName || `COG ${tenant.cogId}`}
        </h2>
        <Badge bg={environmentClassBadgeVariant(tenant.environment)}>{classLabel}</Badge>
        {!tenant.enabled ? (
          <Badge bg="secondary" text="light">
            Not enabled
          </Badge>
        ) : null}
      </Card.Header>
      <Card.Body>
        <p className="fw-semibold" data-testid="fema-credential-headline">
          {description.headline}
        </p>
        {description.detail ? <p className="small">{description.detail}</p> : null}

        {tenant.active ? (
          <CredentialFacts
            title="Active certificate"
            facts={tenant.active}
            cogId={tenant.cogId}
            lastValidatedAt={tenant.lastValidatedAt}
          />
        ) : null}
        {tenant.replacement ? (
          <CredentialFacts
            title="Replacement certificate"
            facts={tenant.replacement}
            cogId={tenant.cogId}
            lastValidatedAt={tenant.replacement.validatedAt}
          />
        ) : null}

        {description.actions.length > 0 ? (
          <div className="d-flex flex-wrap gap-2">
            {description.actions.map((action) => (
              <Button
                key={action}
                variant={action === 'replace' ? 'outline-primary' : 'primary'}
                disabled={busy}
                onClick={() => onAction(action, tenant)}
              >
                {FEMA_CREDENTIAL_ACTION_LABELS[action]}
              </Button>
            ))}
          </div>
        ) : null}
      </Card.Body>
    </Card>
  );
};

export default CredentialStatusCard;
